/**
 * Sales Decisions — the Decision Center
 *
 * v1 surface that turns the comparison engine's signals into ranked
 * action items. This page is intentionally not "another dashboard" —
 * it answers, in order:
 *
 *    1. What should I act on today?       (Row 1 — Top 3 hero cards)
 *    2. Why does it matter?                (Row 2 — Signal bento)
 *    3. Where can I drill down?            (deep-link CTA on every card)
 *
 * Out-of-scope for v1 (will land as Row 3 in v2):
 *    Leakage / Discounts / Service Charge signals.
 *
 * Visual direction: bento layout, dark, large action cards, subtle
 * coloured signal tags, no clutter, no giant tables.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Lightning, TrendUp, TrendDown, Sparkle, ArrowUpRight,
  Package, Handshake, Clock, ShieldCheck, ChartLineUp,
  Percent, HandCoins, ChartPieSlice, Coins,
} from '@phosphor-icons/react';
import api from '../lib/api';
import { toast } from 'sonner';
import DateRangeFilter from '../components/DateRangeFilter';
import { resolveInitialRange, isValidISO, fmtDDMMYYYY } from '../lib/dateRange';
import {
  DecisionTile, ItemMomentumMap, ChannelShiftFlow, DaypartHeatStrip,
  MoneyLeakInfographic, TodayPlaybook,
  mapTopDecisionsToTiles, mapItemMoversToMomentumBubbles,
  mapPartnerShiftsToChannelFlows, buildChannelInsight,
  mapDaypartShiftsToHeatRows, buildDaypartCallout,
  mapLeakageSignalsToMoneyLeakSections, buildTodayPlaybook,
} from '../components/decisions/DecisionsInfographic';

// ──────────────────────────────────────────────────────────────────
// Tiny helpers
// ──────────────────────────────────────────────────────────────────

const fmtPct = (p) => {
  if (p == null || isNaN(p)) return '—';
  const abs = Math.abs(p);
  return `${abs < 10 ? abs.toFixed(1) : Math.round(abs)}%`;
};

const fmtPP = (pp) => {
  if (pp == null || isNaN(pp)) return '—';
  const v = Number(pp);
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}pp`;
};

const fmtINR = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (Math.abs(v) >= 1000)   return `₹${(v / 1000).toFixed(1)}k`;
  return `₹${v.toFixed(0)}`;
};

const fmtInt = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('en-IN'));

// Tone for signal subtype — single source of truth so every surface
// (hero cards + signal rows) colours signals identically.
const TONE = {
  up:   { fg: 'text-emerald-300', bg: 'bg-emerald-500/10',  border: 'border-emerald-500/20', dot: 'bg-emerald-400', Icon: TrendUp,   label: 'Growing'  },
  down: { fg: 'text-rose-300',    bg: 'bg-rose-500/10',     border: 'border-rose-500/20',    dot: 'bg-rose-400',    Icon: TrendDown, label: 'Falling'  },
  new:  { fg: 'text-cyan-300',    bg: 'bg-cyan-500/10',     border: 'border-cyan-500/20',    dot: 'bg-cyan-400',    Icon: Sparkle,   label: 'New'      },
  flat: { fg: 'text-zinc-400',    bg: 'bg-zinc-500/10',     border: 'border-zinc-500/20',    dot: 'bg-zinc-500',    Icon: null,      label: 'Flat'     },
};

const KIND_BADGE = {
  item:        { Icon: Package,   label: 'Item',     fg: 'text-amber-300',   bg: 'bg-amber-500/10' },
  partner:     { Icon: Handshake, label: 'Partner',  fg: 'text-blue-300',    bg: 'bg-blue-500/10' },
  time_of_day: { Icon: Clock,     label: 'Time',     fg: 'text-violet-300',  bg: 'bg-violet-500/10' },
};

const inferKind = (type) => {
  if (!type) return 'item';
  if (type.startsWith('partner_')) return 'partner';
  if (type.startsWith('time_')) return 'time_of_day';
  return 'item';
};
const inferSubtype = (type) => (type ? type.split('_').pop() : 'up');


// ──────────────────────────────────────────────────────────────────
// Hero action card (Row 1)
// ──────────────────────────────────────────────────────────────────

function ActionCard({ action }) {
  const navigate = useNavigate();
  const kind = inferKind(action.type);
  const subtype = inferSubtype(action.type);
  const kindBadge = KIND_BADGE[kind] || KIND_BADGE.item;
  const tone = TONE[subtype] || TONE.up;
  const KindIcon = kindBadge.Icon;
  const ToneIcon = tone.Icon;

  return (
    <article
      className={`group relative overflow-hidden rounded-xl border border-white/5
                  bg-gradient-to-br from-white/[0.04] to-white/[0.01]
                  p-5 transition-all hover:border-white/15
                  hover:from-white/[0.06] hover:to-white/[0.02]`}
      data-testid={`decisions-action-${action.rank}`}
      data-action-type={action.type}
      data-action-score={action.score}
    >
      {/* Subtle accent stripe pinned to subtype tone */}
      <span className={`absolute top-0 left-0 h-0.5 w-full ${tone.bg}`} aria-hidden="true" />

      {/* Header line: rank · kind · tone · confidence */}
      <header className="flex items-center gap-2 mb-3">
        <span className="text-[18px] font-mono font-light text-[#D4FF00] tabular-nums">
          {String(action.rank).padStart(2, '0')}
        </span>
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${kindBadge.bg} ${kindBadge.fg}`}>
          <KindIcon size={11} weight="duotone" /> {kindBadge.label}
        </span>
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${tone.bg} ${tone.fg}`}>
          {ToneIcon && <ToneIcon size={11} weight="bold" />} {tone.label}
        </span>
        <span className="ml-auto flex items-center gap-1.5 text-[10px] font-mono text-zinc-500"
              title="Confidence based on sample size + signal strength">
          <ShieldCheck size={11} /> {action.confidence}%
        </span>
      </header>

      {/* Title — the decisive thing */}
      <h3 className="text-[15px] leading-snug text-zinc-50 font-medium mb-2 line-clamp-2">
        {action.title}
      </h3>

      {/* Why */}
      <p className="text-[12.5px] leading-relaxed text-zinc-400 mb-3 line-clamp-3">
        {action.why}
      </p>

      {/* Suggested action — italicised so the eye finds it */}
      <p className="text-[11.5px] leading-snug italic text-zinc-500 mb-4 line-clamp-2"
         data-testid={`decisions-action-${action.rank}-suggested`}>
        → {action.suggested_action}
      </p>

      {/* Footer: impact pill + deep-link CTA */}
      <footer className="flex items-center justify-between mt-auto">
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider
                      ${action.impact === 'High'   ? 'bg-rose-500/10 text-rose-300'   :
                       action.impact === 'Medium' ? 'bg-amber-500/10 text-amber-300' :
                                                    'bg-zinc-500/10 text-zinc-400'}`}
          title="Impact bucketed from absolute volume"
        >
          {action.impact} impact
        </span>
        <button
          type="button"
          onClick={() => navigate(action.deep_link)}
          className="flex items-center gap-1 text-[11px] font-mono text-[#D4FF00]/80
                     hover:text-[#D4FF00] transition-colors"
          data-testid={`decisions-action-${action.rank}-cta`}
        >
          Investigate <ArrowUpRight size={12} weight="bold" />
        </button>
      </footer>
    </article>
  );
}


// ──────────────────────────────────────────────────────────────────
// Signal bento (Row 2)
// ──────────────────────────────────────────────────────────────────

function SignalRow({ subtype, primary, secondary, deepLink, testIdPrefix, idx }) {
  const navigate = useNavigate();
  const tone = TONE[subtype] || TONE.flat;
  const ToneIcon = tone.Icon;
  return (
    <li
      role={deepLink ? 'button' : undefined}
      tabIndex={deepLink ? 0 : undefined}
      onClick={deepLink ? () => navigate(deepLink) : undefined}
      onKeyDown={deepLink ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(deepLink); } } : undefined}
      className={`flex items-center gap-3 px-3 py-2 -mx-3 rounded-md
                  ${deepLink ? 'cursor-pointer transition-colors hover:bg-white/[0.04]' : ''}`}
      data-testid={`${testIdPrefix}-${idx}`}
    >
      <span className={`flex items-center justify-center w-6 h-6 rounded ${tone.bg}`}>
        {ToneIcon && <ToneIcon size={12} weight="bold" className={tone.fg} />}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-zinc-200 truncate">{primary}</p>
        <p className="text-[10px] text-zinc-500 truncate font-mono">{secondary}</p>
      </div>
    </li>
  );
}

function ItemMoversCard({ signals }) {
  return (
    <section
      className="rounded-xl border border-white/5 bg-white/[0.02] p-4"
      data-testid="decisions-bento-items"
    >
      <header className="flex items-center gap-2 mb-3">
        <Package size={14} className="text-amber-300" weight="duotone" />
        <h3 className="text-[12px] uppercase tracking-widest font-mono text-zinc-300">Item Movers</h3>
        <span className="ml-auto text-[10px] font-mono text-zinc-500">{signals.length}</span>
      </header>
      {signals.length === 0 ? (
        <p className="text-[12px] text-zinc-500 py-2">No notable item shifts in this range.</p>
      ) : (
        <ul className="space-y-0.5">
          {signals.map((s, idx) => (
            <SignalRow
              key={`${s.item_name_normalized}-${idx}`}
              subtype={s.subtype}
              primary={s.item_name}
              secondary={`${fmtInt(s.current)} qty · ${s.pct != null ? `${s.pct > 0 ? '+' : ''}${s.pct.toFixed(1)}%` : 'new'} vs prev ${fmtInt(s.previous)}`}
              deepLink={`/explorer?tab=items&item=${encodeURIComponent(s.item_name_normalized || '')}`}
              testIdPrefix="decisions-signal-item"
              idx={idx}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function PartnerShiftsCard({ signals }) {
  return (
    <section
      className="rounded-xl border border-white/5 bg-white/[0.02] p-4"
      data-testid="decisions-bento-partners"
    >
      <header className="flex items-center gap-2 mb-3">
        <Handshake size={14} className="text-blue-300" weight="duotone" />
        <h3 className="text-[12px] uppercase tracking-widest font-mono text-zinc-300">Partner Shifts</h3>
        <span className="ml-auto text-[10px] font-mono text-zinc-500">{signals.length}</span>
      </header>
      {signals.length === 0 ? (
        <p className="text-[12px] text-zinc-500 py-2">No notable partner movement.</p>
      ) : (
        <ul className="space-y-0.5">
          {signals.map((s, idx) => (
            <SignalRow
              key={`${s.partner_id}-${idx}`}
              subtype={s.subtype}
              primary={s.partner_name}
              secondary={`${fmtInt(s.current_orders)} orders · ${s.pct != null ? `${s.pct > 0 ? '+' : ''}${s.pct.toFixed(1)}%` : 'new'} · share ${s.share_pct_current}%`}
              deepLink={`/explorer?tab=partners&partner_id=${encodeURIComponent(s.partner_id || '')}`}
              testIdPrefix="decisions-signal-partner"
              idx={idx}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function WindowVerdictPill({ verdict }) {
  if (!verdict) return null;
  const STATUS_TONE = {
    healthy:           { fg: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', dot: 'bg-emerald-400' },
    watch:             { fg: 'text-amber-300',   bg: 'bg-amber-500/10',   border: 'border-amber-500/25',   dot: 'bg-amber-400' },
    at_risk:           { fg: 'text-rose-300',    bg: 'bg-rose-500/10',    border: 'border-rose-500/25',    dot: 'bg-rose-400' },
    insufficient_data: { fg: 'text-zinc-400',    bg: 'bg-zinc-500/10',    border: 'border-zinc-500/20',    dot: 'bg-zinc-500' },
  };
  const tone = STATUS_TONE[verdict.status] || STATUS_TONE.insufficient_data;
  return (
    <div
      className={`flex items-start gap-2 px-2.5 py-1.5 mb-2 rounded-md border ${tone.border} ${tone.bg}`}
      data-testid="decisions-window-verdict"
      data-status={verdict.status}
    >
      <span className={`mt-1 h-1.5 w-1.5 rounded-full ${tone.dot} shrink-0`} aria-hidden />
      <div className="text-[11.5px] leading-snug">
        <span className={`font-mono uppercase tracking-widest text-[10px] ${tone.fg} mr-1.5`}>
          {verdict.scope_label || (verdict.scope === 'windowed' ? 'Window' : 'Full day')} ·
        </span>
        <span className="text-zinc-200" data-testid="decisions-window-verdict-summary">
          {verdict.summary}
        </span>
      </div>
    </div>
  );
}


function TimeShiftsCard({ signals, bucket, onBucketChange, hourStart, hourEnd, onWindowChange, windowHealth, refetching }) {
  const BUCKETS = [
    { value: 'daypart', label: 'DAYPART' },
    { value: 'hour',    label: 'HOUR' },
    { value: '2h',      label: '2H' },
    { value: 'half',    label: 'HALF' },
  ];
  const hasWindow = hourStart != null && hourEnd != null;
  const fmtHr = (h) => {
    if (h == null) return '';
    const hour = h % 24;
    const suffix = hour < 12 ? 'AM' : 'PM';
    const h12 = hour % 12 || 12;
    return `${h12} ${suffix}`;
  };
  // Start dropdown: 0..23. End dropdown: 1..24.
  const startOptions = Array.from({ length: 24 }, (_, i) => i);
  const endOptions = Array.from({ length: 24 }, (_, i) => i + 1);
  const startVal = hourStart != null ? hourStart : '';
  const endVal = hourEnd != null ? hourEnd : '';

  const handleStart = (v) => {
    const s = v === '' ? null : parseInt(v, 10);
    let e = hourEnd;
    // Auto-bump end if it would be invalid (start must be < end).
    if (s != null && e != null && s >= e) e = Math.min(24, s + 1);
    onWindowChange?.(s, e);
  };
  const handleEnd = (v) => {
    const e = v === '' ? null : parseInt(v, 10);
    let s = hourStart;
    if (s != null && e != null && s >= e) s = Math.max(0, e - 1);
    onWindowChange?.(s, e);
  };

  return (
    <section
      className="rounded-xl border border-white/5 bg-white/[0.02] p-4"
      data-testid="decisions-bento-time"
      aria-busy={refetching ? 'true' : 'false'}
    >
      <header className="flex items-center gap-2 mb-2 flex-wrap">
        <Clock size={14} className="text-violet-300" weight="duotone" />
        <h3 className="text-[12px] uppercase tracking-widest font-mono text-zinc-300">Time-of-Day Shifts</h3>
        <span className="text-[10px] font-mono text-zinc-500">{signals.length}</span>
        {refetching && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-mono text-violet-200 bg-violet-500/10 border border-violet-500/25"
            data-testid="decisions-time-updating"
            aria-live="polite"
          >
            <span className="h-1 w-1 rounded-full bg-violet-400 animate-pulse" />
            Updating
          </span>
        )}
        <div
          className="ml-auto inline-flex items-center rounded-md border border-white/5 bg-black/40 p-0.5"
          role="tablist"
          aria-label="Time bucket granularity"
          data-testid="decisions-time-bucket"
        >
          {BUCKETS.map((b) => {
            const active = bucket === b.value;
            return (
              <button
                key={b.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onBucketChange?.(b.value)}
                className={`px-2 py-0.5 text-[10px] font-mono tracking-wider rounded transition-colors ${
                  active
                    ? 'bg-violet-500/20 text-violet-200'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
                data-testid={`decisions-time-bucket-${b.value}`}
              >
                {b.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Time Window control — same row of controls but logically separate. */}
      <div
        className="flex items-center gap-2 mb-3 flex-wrap"
        data-testid="decisions-time-window"
      >
        <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">
          Time Window
        </span>
        <select
          value={startVal}
          onChange={(e) => handleStart(e.target.value)}
          data-testid="decisions-time-window-start"
          className="text-[11px] font-mono bg-black/40 border border-white/5 text-zinc-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-violet-500/40"
        >
          <option value="">—</option>
          {startOptions.map((h) => (
            <option key={`s-${h}`} value={h}>{fmtHr(h)}</option>
          ))}
        </select>
        <span className="text-[11px] text-zinc-600">→</span>
        <select
          value={endVal}
          onChange={(e) => handleEnd(e.target.value)}
          data-testid="decisions-time-window-end"
          className="text-[11px] font-mono bg-black/40 border border-white/5 text-zinc-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-violet-500/40"
        >
          <option value="">—</option>
          {endOptions.map((h) => (
            <option key={`e-${h}`} value={h}>{h === 24 ? '12 AM (+1)' : fmtHr(h)}</option>
          ))}
        </select>
        {hasWindow && (
          <>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-violet-500/15 text-violet-200 border border-violet-500/30"
              data-testid="decisions-time-window-pill"
            >
              {fmtHr(hourStart)}–{hourEnd === 24 ? '12 AM' : fmtHr(hourEnd)}
            </span>
            <button
              type="button"
              onClick={() => onWindowChange?.(null, null)}
              className="text-[10px] font-mono text-zinc-500 hover:text-zinc-200 underline-offset-2 hover:underline"
              data-testid="decisions-time-window-clear"
            >
              Clear
            </button>
          </>
        )}
      </div>

      {/* Window Health Verdict — sales-intelligence-only one-line summary */}
      <WindowVerdictPill verdict={windowHealth} />

      <div className={`transition-opacity duration-150 ${refetching ? 'opacity-50' : 'opacity-100'}`}>
      {signals.length === 0 ? (
        <p className="text-[12px] text-zinc-500 py-2" data-testid="decisions-time-empty">
          {hasWindow
            ? 'No notable shifts in this time window.'
            : 'No notable shifts at this granularity.'}
        </p>
      ) : (
        <ul className="space-y-0.5">
          {signals.map((s, idx) => {
            const ledBy = (bucket !== 'hour' && s.dominant_hour_label && (s.dominant_hour_share || 0) >= 0.3)
              ? ` · led by ${s.dominant_hour_label}`
              : '';
            const pctText = s.pct != null
              ? `${s.pct > 0 ? '+' : ''}${s.pct.toFixed(1)}%`
              : 'new';
            return (
              <SignalRow
                key={`${s.bucket_label || s.label}-${idx}`}
                subtype={s.subtype}
                primary={s.bucket_label || s.label}
                secondary={`${fmtInt(s.current_orders)} orders · ${pctText} vs prev ${fmtInt(s.previous_orders)}${ledBy}`}
                deepLink={`/explorer?tab=hourly`}
                testIdPrefix="decisions-signal-time"
                idx={idx}
              />
            );
          })}
        </ul>
      )}
      </div>
    </section>
  );
}


// ──────────────────────────────────────────────────────────────────
// Row 3 — Revenue Protection + Commercial Leakage (v2)
// ──────────────────────────────────────────────────────────────────
//
// Four compact bento cards living strictly inside the Commercial
// Intelligence boundary: discounts / service-charge waivers /
// aggregator share / monetization quality. NO prep / kitchen /
// inventory signals.
//
// Each row clickable → deep-links to the relevant existing surface
// using the canonical URL contract (date_from / date_to / partner_id).

const LeakageRow = ({ subtype, primary, secondary, tertiary, deepLink, testId }) => {
  const navigate = useNavigate();
  const tone = TONE[subtype] || TONE.flat;
  const ToneIcon = tone.Icon;
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={() => navigate(deepLink)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(deepLink); } }}
      className="flex items-center gap-3 px-3 py-2 -mx-3 rounded-md cursor-pointer transition-colors hover:bg-white/[0.04]"
      data-testid={testId}
    >
      <span className={`flex items-center justify-center w-6 h-6 rounded ${tone.bg}`}>
        {ToneIcon && <ToneIcon size={12} weight="bold" className={tone.fg} />}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-zinc-200 truncate">{primary}</p>
        <p className="text-[10px] text-zinc-500 truncate font-mono">{secondary}</p>
      </div>
      {tertiary && (
        <span className="text-[10px] font-mono text-zinc-500 shrink-0">{tertiary}</span>
      )}
    </li>
  );
};

const CardShell = ({ icon: Icon, iconClass, title, count, accent, testId, children }) => (
  <section
    className={`rounded-xl border border-white/5 bg-white/[0.02] p-4 ${accent || ''}`}
    data-testid={testId}
  >
    <header className="flex items-center gap-2 mb-3">
      <Icon size={14} className={iconClass} weight="duotone" />
      <h3 className="text-[12px] uppercase tracking-widest font-mono text-zinc-300">{title}</h3>
      <span className="ml-auto text-[10px] font-mono text-zinc-500">{count}</span>
    </header>
    {children}
  </section>
);

function DiscountLeakageCard({ signals }) {
  return (
    <CardShell
      icon={Percent}
      iconClass="text-amber-300"
      title="Discount Leakage"
      count={signals.length}
      testId="decisions-bento-discounts"
    >
      {signals.length === 0 ? (
        <p className="text-[12px] text-zinc-500 py-2">No notable discount leakage in this range.</p>
      ) : (
        <ul className="space-y-0.5">
          {signals.map((s, idx) => (
            <LeakageRow
              key={`${s.scope}-${s.label}-${idx}`}
              subtype={s.subtype}
              primary={s.label}
              secondary={`${s.current_rate_pct.toFixed(1)}% rate · ${fmtPP(s.rate_delta_pp)} vs prev · ${fmtINR(s.current_amount)} foregone`}
              tertiary={s.scope}
              deepLink={s.deep_link}
              testId={`decisions-signal-discount-${idx}`}
            />
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function ServiceChargeCard({ signals }) {
  return (
    <CardShell
      icon={HandCoins}
      iconClass="text-amber-300"
      title="Service-Charge Waivers"
      count={signals.length}
      testId="decisions-bento-service-charge"
    >
      {signals.length === 0 ? (
        <p className="text-[12px] text-zinc-500 py-2">No notable waiver-rate shifts.</p>
      ) : (
        <ul className="space-y-0.5">
          {signals.map((s, idx) => (
            <LeakageRow
              key={`${s.scope}-${s.label}-${idx}`}
              subtype={s.subtype}
              primary={s.label}
              secondary={`${s.current_rate_pct.toFixed(1)}% rate · ${fmtPP(s.rate_delta_pp)} vs prev · ${s.current_waivers}/${s.eligible_dine_in_current} waivers · ${fmtINR(s.current_amount)}`}
              tertiary={s.scope}
              deepLink={s.deep_link}
              testId={`decisions-signal-sc-${idx}`}
            />
          ))}
        </ul>
      )}
    </CardShell>
  );
}

// Aggregator Share Shifts re-uses the partner signals but with a
// share-shift framing. We surface partners whose share_pct_delta is
// the largest (in either direction) so the card answers "who is
// gaining/losing share, regardless of absolute volume direction".
function AggregatorShareCard({ partnerSignals }) {
  const ranked = (partnerSignals || [])
    .slice()
    .sort((a, b) => Math.abs(b.share_pct_delta || 0) - Math.abs(a.share_pct_delta || 0))
    .filter((s) => Math.abs(s.share_pct_delta || 0) >= 1.0)
    .slice(0, 6);
  return (
    <CardShell
      icon={ChartPieSlice}
      iconClass="text-blue-300"
      title="Aggregator Share Shifts"
      count={ranked.length}
      testId="decisions-bento-aggregator"
    >
      {ranked.length === 0 ? (
        <p className="text-[12px] text-zinc-500 py-2">Channel share is stable across partners.</p>
      ) : (
        <ul className="space-y-0.5">
          {ranked.map((s, idx) => {
            const dir = s.share_pct_delta >= 0 ? 'up' : 'down';
            return (
              <LeakageRow
                key={`${s.partner_id}-${idx}`}
                subtype={dir}
                primary={s.partner_name}
                secondary={`${s.share_pct_current}% share · ${fmtPP(s.share_pct_delta)} vs prev · ${fmtInt(s.current_orders)} orders`}
                tertiary={`${fmtPct(s.pct)} orders`}
                deepLink={`/explorer?tab=partners&partner_id=${encodeURIComponent(s.partner_id || '')}`}
                testId={`decisions-signal-aggregator-${idx}`}
              />
            );
          })}
        </ul>
      )}
    </CardShell>
  );
}

function RevenueQualityCard({ signals }) {
  return (
    <CardShell
      icon={Coins}
      iconClass="text-emerald-300"
      title="Revenue Quality / AOV"
      count={signals.length}
      testId="decisions-bento-revenue-quality"
    >
      {signals.length === 0 ? (
        <p className="text-[12px] text-zinc-500 py-2">Monetization is stable across the mix.</p>
      ) : (
        <ul className="space-y-0.5">
          {signals.map((s, idx) => {
            let secondary;
            if (s.scope === 'orders_vs_revenue') {
              // orders_pct / revenue_pct are percentages; gap is in pp.
              secondary = `orders ${s.orders_pct >= 0 ? '+' : ''}${fmtPct(s.orders_pct)} vs revenue ${s.revenue_pct >= 0 ? '+' : ''}${fmtPct(s.revenue_pct)} · gap ${fmtPP(s.gap_pp)}`;
            } else {
              // aov_overall / aov_partner — `pct` is a % change, not pp.
              const sign = (s.pct ?? 0) >= 0 ? '+' : '\u2212';
              secondary = `${fmtINR(s.current_value)} · ${sign}${fmtPct(s.pct)} vs prev · ${fmtInt(s.current_orders)} orders`;
            }
            return (
              <LeakageRow
                key={`${s.scope}-${idx}`}
                subtype={s.subtype}
                primary={s.label}
                secondary={secondary}
                tertiary={s.scope.replace('aov_', '')}
                deepLink={s.deep_link}
                testId={`decisions-signal-revenue-quality-${idx}`}
              />
            );
          })}
        </ul>
      )}
    </CardShell>
  );
}


// ──────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────

export default function SalesDecisionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = resolveInitialRange(searchParams);
  const initialBucket = (() => {
    const tb = searchParams.get('time_bucket');
    return ['daypart', 'hour', '2h', 'half'].includes(tb) ? tb : 'daypart';
  })();
  const parseHour = (raw, max) => {
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0 || n > max) return null;
    return n;
  };
  const initialHourStartRaw = parseHour(searchParams.get('hour_start'), 23);
  const initialHourEndRaw = parseHour(searchParams.get('hour_end'), 24);
  // Both must be present and valid (start < end). Else clear both.
  const validInitialWindow = (
    initialHourStartRaw != null &&
    initialHourEndRaw != null &&
    initialHourStartRaw < initialHourEndRaw
  );
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo,   setDateTo]   = useState(initial.to);
  const [timeBucket, setTimeBucket] = useState(initialBucket);
  const [hourStart, setHourStart] = useState(validInitialWindow ? initialHourStartRaw : null);
  const [hourEnd,   setHourEnd]   = useState(validInitialWindow ? initialHourEndRaw   : null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // URL sync — canonical date contract + bucket + window.
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set('date_from', dateFrom);
    sp.set('date_to', dateTo);
    sp.set('time_bucket', timeBucket);
    if (hourStart != null && hourEnd != null) {
      sp.set('hour_start', String(hourStart));
      sp.set('hour_end',   String(hourEnd));
    } else {
      sp.delete('hour_start');
      sp.delete('hour_end');
    }
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, timeBucket, hourStart, hourEnd]);

  const handleRangeChange = (from, to) => {
    if (!isValidISO(from) || !isValidISO(to)) {
      toast.error('Invalid dates'); return;
    }
    setDateFrom(from); setDateTo(to);
  };

  const handleWindowChange = (s, e) => {
    // Both null → clear. Otherwise both must be valid + ordered.
    if (s == null && e == null) {
      setHourStart(null); setHourEnd(null); return;
    }
    if (s != null && e != null && s >= e) {
      toast.error('Window end must be after start'); return;
    }
    setHourStart(s); setHourEnd(e);
  };

  useEffect(() => {
    // AbortController cancels the previous in-flight request so
    // rapid clicks (bucket/window/date) don't pile up. The cleanup
    // both flips `alive` (defensive) and aborts the axios call (so
    // the network connection actually closes — important on slow
    // prod connections where responses can arrive 8-10s late).
    const controller = new AbortController();
    let alive = true;
    setLoading(true); setError(null);
    const params = { date_from: dateFrom, date_to: dateTo, time_bucket: timeBucket };
    if (hourStart != null && hourEnd != null) {
      params.hour_start = hourStart;
      params.hour_end   = hourEnd;
    }
    api.get('/sales/decisions', { params, signal: controller.signal })
      .then((res) => { if (alive) setData(res.data); })
      .catch((e) => {
        if (!alive) return;
        // axios marks aborted requests as `e.code === 'ERR_CANCELED'`
        // or `e.name === 'CanceledError'` — treat as a non-error
        // (just an effect-cleanup), don't toast or set error state.
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setError(e?.response?.data?.detail || 'Failed to load decisions');
        toast.error('Failed to load Decision Center');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [dateFrom, dateTo, timeBucket, hourStart, hourEnd]);

  const topActions = data?.top_actions || [];
  const groups = data?.signal_groups || {
    items: [], partners: [], time_of_day: [],
    discounts: [], service_charge: [], revenue_quality: [],
  };
  const compare = data?.comparison;
  // Distinguish two loading flavours:
  //   isInitialLoading — no data yet, show full skeletons (first page open).
  //   isRefetching     — already have data, fetching new — keep old data
  //                      visible but show subtle "Updating…" badges so the
  //                      user knows the click registered.
  const isInitialLoading = loading && data == null;
  const isRefetching     = loading && data != null;

  // ── Map raw decisions payload → infographic component props ──
  // Pure useMemo derivations — keep mappers stateless so the visual
  // layer never has to think about data semantics.
  const decisionTiles   = useMemo(() => mapTopDecisionsToTiles(topActions), [topActions]);
  const momentumBubbles = useMemo(() => mapItemMoversToMomentumBubbles(groups.items),  [groups.items]);
  const channelFlows    = useMemo(() => mapPartnerShiftsToChannelFlows(groups.partners), [groups.partners]);
  const channelInsight  = useMemo(() => buildChannelInsight(channelFlows), [channelFlows]);
  const heatRows        = useMemo(() => mapDaypartShiftsToHeatRows(groups.time_of_day), [groups.time_of_day]);
  const daypartCallout  = useMemo(() => buildDaypartCallout(heatRows), [heatRows]);
  const leakage = useMemo(() => {
    // Derive current outlet revenue from any signal that carries it
    // — the discount-drift signal always does. Fallback to AOV-overall.
    const driftSig = (groups.discounts || []).find(s => s.scope === 'drift');
    const aovSig   = (groups.revenue_quality || []).find(s => s.scope === 'aov_overall');
    const currentRevenue = driftSig?.current_revenue || aovSig?.current_revenue || 0;
    const prevRevenue    = driftSig?.previous_revenue || 0;
    const sections = mapLeakageSignalsToMoneyLeakSections(groups, currentRevenue);
    // Pull a few human leak insights from existing signals.
    const insights = [];
    if (driftSig) {
      const ratio = driftSig.discount_growth_pct && driftSig.revenue_growth_pct
        ? (driftSig.discount_growth_pct / Math.max(Math.abs(driftSig.revenue_growth_pct), 1)).toFixed(1)
        : null;
      if (ratio) insights.push(`Discount growth is ${ratio}× revenue growth`);
    }
    const hotHour = (groups.discounts || []).find(s => s.scope === 'hour' && s.subtype === 'up');
    if (hotHour) insights.push(`High-discount window: ${hotHour.label}`);
    const scHour  = (groups.service_charge || []).find(s => s.scope === 'hour' && s.subtype === 'up');
    if (scHour) insights.push(`Service-charge waivers spiking: ${scHour.label}`);
    const aggSig  = (groups.partners || []).find(s => s.subtype === 'down');
    if (aggSig) insights.push(`${aggSig.partner_name} share down ${aggSig.share_pct_delta?.toFixed(1)}pp`);
    // Total-at-risk delta — best proxy is discount-amount delta vs prev.
    let totalDeltaPct = null;
    if (driftSig?.previous_amount > 0 && driftSig?.current_amount != null) {
      totalDeltaPct = Math.round(
        ((driftSig.current_amount - driftSig.previous_amount) / driftSig.previous_amount) * 100,
      );
    }
    return { ...sections, insights, totalDeltaPct };
  }, [groups]);
  const playbook = useMemo(() => buildTodayPlaybook(topActions, groups), [topActions, groups]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 p-4 md:p-6" data-testid="decisions-page">
      {/* Header — title + range filter */}
      <header className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-[#D4FF00]/10 border border-[#D4FF00]/20">
            <Lightning size={22} className="text-[#D4FF00]" weight="duotone" />
          </div>
          <div>
            <h1 className="text-2xl md:text-[26px] font-semibold font-['Outfit'] tracking-tight text-zinc-50">
              Sales Decisions
            </h1>
            <p className="text-[12.5px] text-zinc-500 mt-0.5" data-testid="decisions-subtitle">
              The {Math.min(topActions.length, 3) || 3} moves your data is asking for · {fmtDDMMYYYY(dateFrom)} → {fmtDDMMYYYY(dateTo)}
            </p>
            {compare?.previous_from && compare?.previous_to && (
              <p
                className="text-[11px] text-zinc-600 mt-0.5 font-mono"
                data-testid="decisions-comparison-caption"
              >
                Comparing{' '}
                <span className="text-zinc-400">
                  {fmtDDMMYYYY(compare.current_from)} → {fmtDDMMYYYY(compare.current_to)}
                </span>
                <span className="mx-2 text-zinc-700">vs</span>
                <span className="text-zinc-400">
                  {fmtDDMMYYYY(compare.previous_from)} → {fmtDDMMYYYY(compare.previous_to)}
                </span>
                {compare.label && (
                  <span className="ml-2 text-zinc-700">· {compare.label}</span>
                )}
              </p>
            )}
          </div>
        </div>
        <DateRangeFilter
          value={{ from: dateFrom, to: dateTo }}
          onChange={handleRangeChange}
          testIdPrefix="decisions-range"
        />
      </header>

      {error && (
        <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-4 text-rose-200 text-[13px] mb-6" data-testid="decisions-error">
          {error}
        </div>
      )}

      {/* ROW 1 — Today's Top 3 Decisions (infographic tiles) */}
      <section className="mb-6" data-testid="decisions-row-actions">
        <div className="flex items-center gap-2 mb-3">
          <Lightning size={14} className="text-[#D4FF00]" weight="fill" />
          <h2 className="text-[11px] uppercase tracking-[0.15em] font-mono text-zinc-300">
            Today&apos;s top 3 decisions
          </h2>
          {isRefetching && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono text-violet-200 bg-violet-500/10 border border-violet-500/25"
              data-testid="decisions-refetching"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-violet-400 animate-pulse" />
              Updating…
            </span>
          )}
        </div>

        {isInitialLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="decisions-actions-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-56 rounded-xl border border-white/5 bg-white/[0.02] animate-pulse" />
            ))}
          </div>
        )}

        {!isInitialLoading && topActions.length === 0 && (
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-8 text-center" data-testid="decisions-actions-empty">
            <p className="text-[13px] text-zinc-400">
              No notable shifts to act on in this window.
            </p>
            <p className="text-[11px] text-zinc-600 mt-1">
              Try widening the date range or comparing against a fuller previous period.
            </p>
          </div>
        )}

        {!isInitialLoading && topActions.length > 0 && (
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 transition-opacity ${isRefetching ? 'opacity-60' : 'opacity-100'}`}>
            {decisionTiles.map((tile) => (
              <DecisionTile
                key={tile.rank}
                rank={tile.rank}
                category={tile.category}
                icon={tile.icon}
                tone={tile.tone}
                punchline={tile.punchline}
                hero={tile.hero}
                subMetric={tile.subMetric}
                impact={tile.impact}
                confidence={tile.confidence}
                cta={tile.cta}
                onCta={() => tile.deepLink && navigate(tile.deepLink)}
                testid={`decision-tile-${tile.rank}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* ROW 2 — Why It Is Happening */}
      <section className="mb-6" data-testid="decisions-row-signals">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={14} className="text-zinc-400" weight="fill" />
          <h2 className="text-[11px] uppercase tracking-[0.15em] font-mono text-zinc-300">
            Why it is happening
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ItemMomentumMap
            movers={momentumBubbles}
            onViewAll={() => navigate(`/explorer?date_from=${dateFrom}&date_to=${dateTo}`)}
          />
          <ChannelShiftFlow
            shifts={channelFlows}
            insight={channelInsight}
          />
          <DaypartHeatStrip
            rows={heatRows}
            callout={daypartCallout}
            onCta={() => navigate(`/explorer?tab=hourly&date_from=${dateFrom}&date_to=${dateTo}`)}
          />
        </div>
      </section>

      {/* ROW 3 — Where Money Is Leaking */}
      <section className="mb-6" data-testid="decisions-row-leakage">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={14} className="text-rose-300" weight="fill" />
          <h2 className="text-[11px] uppercase tracking-[0.15em] font-mono text-zinc-300">
            Where money is leaking
          </h2>
        </div>
        <MoneyLeakInfographic
          total={leakage.total}
          totalDeltaPct={leakage.totalDeltaPct}
          breakdown={leakage.breakdown}
          perHundred={leakage.perHundred}
          insights={leakage.insights}
          onCta={() => navigate(`/discounts?date_from=${dateFrom}&date_to=${dateTo}`)}
        />
      </section>

      {/* ROW 4 — Today's Playbook */}
      <section data-testid="decisions-row-playbook">
        <TodayPlaybook
          push={playbook.push}
          protect={playbook.protect}
          watch={playbook.watch}
          ask={playbook.ask}
        />
      </section>
    </div>
  );
}
